import { ArrowUUpLeftIcon, ChatCircleTextIcon, CheckSquareIcon, PhoneIcon } from '@phosphor-icons/react'
import { OpenInWindow } from 'iconoir-react'
import Button from 'app/components/Button'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { cn } from 'app/lib/utils'
import { ContactJson } from 'app/types/entities'
import { ActionPlay } from './queueRegistry'
import { useContactMessages } from './useContactMessages'
import { useContactTasks } from './useContactTasks'

/**
 * The floating toolbar under the focus card. Lives outside the card so it is
 * not squeezed by the card's padding, and stays put while the card turns over,
 * so the actions are reachable from the thread.
 */
export function FocusActionBar({
  play,
  backPanel,
  contact,
  phone,
  canClickToCall,
  handleClickToCall,
  recordCall,
  thread,
  tasks,
  openThread,
  openTasks,
  onTurnBack,
  openFollowUpModal,
  openProfile,
}: {
  play?: ActionPlay
  backPanel: `thread` | `tasks` | undefined
  contact: ContactJson
  phone?: string | null
  canClickToCall: boolean
  handleClickToCall: () => void
  recordCall: () => void
  thread: ReturnType<typeof useContactMessages>
  tasks: ReturnType<typeof useContactTasks>
  openThread: () => void
  openTasks: () => void
  onTurnBack: () => void
  openFollowUpModal: () => void
  openProfile: () => void
}) {
  const primaryIsCall = play?.primary === `call`
  const primaryIsText = play?.primary === `text`
  // Style carries the meaning: outlined blue is the play's suggested next
  // move, solid blue is the view currently open, plain white is everything
  // else. Only one button is ever solid.
  const callButton = (suggested: boolean) => {
    const inner = (
      <>
        <PhoneIcon size={16} weight="bold" className="mr-1.5" /> Call
      </>
    )
    const mode = suggested ? `secondary` : `white`
    if (canClickToCall) {
      return (
        <Button mode={mode} onClick={handleClickToCall}>
          {inner}
        </Button>
      )
    }
    if (!phone) return null
    return (
      <a href={`tel:${phone}`} onClick={recordCall}>
        <Button mode={mode}>{inner}</Button>
      </a>
    )
  }
  const textButton = (suggested: boolean) => (
    <Button
      mode={backPanel === `thread` ? `primary` : suggested ? `secondary` : `white`}
      disabled={!contact.cellPhone}
      aria-pressed={backPanel === `thread`}
      onClick={backPanel === `thread` ? onTurnBack : openThread}
    >
      <ChatCircleTextIcon size={16} weight="bold" className="mr-1.5" /> Text
      {thread.unreadCount > 0 ? (
        <span className="ml-1.5 inline-flex h-5 items-center justify-center rounded-full bg-orange-500 px-1.5 text-label-xs leading-none tabular-nums text-on-color">
          {thread.unreadCount} new
        </span>
      ) : thread.messages.length > 0 ? (
        <span
          className={cn(
            `ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-label-xs leading-none tabular-nums`,
            backPanel === `thread` ? `bg-white/25 text-on-color` : `bg-surface-muted text-subtle`,
          )}
        >
          {thread.messages.length}
        </span>
      ) : null}
    </Button>
  )
  return (
    // Hugs its buttons rather than stretching to the card width. Rounded-xl,
    // not a full pill: a pill's corner arc curves in further than the
    // square-cornered buttons at each end, so they bled past its edge.
    <div className="mx-auto mt-4 flex w-fit max-w-full flex-wrap items-center justify-center gap-1.5 rounded-xl border border-default bg-surface p-2 shadow-elevation-4 [&_button]:shrink-0 [&_button]:whitespace-nowrap sm:flex-nowrap">
      {/* Fixed order, always: hoisting the play's suggested action to the
          front made Call and Text trade places between cards, so the
          suggestion is carried by the outlined-blue style instead. */}
      {callButton(primaryIsCall)}
      {textButton(primaryIsText)}
      <Button mode="white" onClick={openFollowUpModal}>
        <ArrowUUpLeftIcon size={16} weight="bold" className="mr-1.5" /> Follow Up
      </Button>
      <Button
        mode={backPanel === `tasks` ? `primary` : `white`}
        aria-pressed={backPanel === `tasks`}
        onClick={backPanel === `tasks` ? onTurnBack : openTasks}
      >
        <CheckSquareIcon size={16} weight="bold" className="mr-1.5" /> Tasks
        {tasks.openCount > 0 && (
          <span
            className={cn(
              `ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-label-xs leading-none tabular-nums`,
              tasks.overdueCount > 0
                ? `bg-red-600 text-on-color`
                : backPanel === `tasks`
                  ? `bg-white/25 text-on-color`
                  : `bg-surface-muted text-subtle`,
            )}
          >
            {tasks.openCount}
          </span>
        )}
      </Button>
      <div className="mx-0.5 hidden h-6 w-px bg-(--line-strong) sm:block" />
      <TooltipWrapper message="Open full profile">
        <Button mode="white" onClick={openProfile} aria-label="Open full profile">
          <OpenInWindow height={16} width={16} />
        </Button>
      </TooltipWrapper>
    </div>
  )
}
